/**
 * SIP Client Wrapper — Thin abstraction over JsSIP.UA
 *
 * All SIP/WebRTC interactions go through this class.
 * Components never import JsSIP directly.
 */
import JsSIP from "jssip";
import { SessionManager } from "./SessionManager";
import { AudioMixer } from "./AudioMixer";

// ---------- Event types ----------
export type SipClientStatus =
  | "idle"
  | "connecting"
  | "registered"
  | "unregistered"
  | "failed";

export interface SipCallInfo {
  id: string;
  direction: "inbound" | "outbound";
  remoteIdentity: string;
  displayName: string;
  startTime: number;
  isOnHold: boolean;
  isMuted: boolean;
}

export type SipClientEvent =
  | "statusChanged"
  | "registered"
  | "unregistered"
  | "registrationFailed"
  | "incomingCall"
  | "calling"
  | "callAccepted"
  | "callConfirmed"
  | "callEnded"
  | "callFailed"
  | "callHold"
  | "callUnhold"
  | "callMute"
  | "callUnmute"
  | "iceStateChanged"
  | "consultCallStarted"
  | "consultCallAccepted"
  | "consultCallEnded"
  | "consultCallFailed"
  | "attendedTransferCompleted"
  | "attendedTransferFailed"
  | "sessionAdded"
  | "sessionRemoved"
  | "sessionUpdated"
  | "remoteStreamUpdated"
  | "activeSessionChanged"
  | "swapStarted"
  | "swapCompleted"
  | "swapFailed"
  | "swapCriticalFailure"
  | "holdFailed"
  | "resumeFailed"
  | "maxSessionsReached"
  | "conferenceStarted"
  | "conferenceUpdated"
  | "conferenceEnded"
  | "multiCallError";

type EventHandler = (...args: any[]) => void;

export interface SipConfig {
  sipUri: string;
  password: string;
  displayName: string;
  wsServer: string;
  iceServers: Array<{ urls: string | string[] }>;
}

// ---------- Class ----------
class SipClient {
  private ua: JsSIP.UA | null = null;
  private socket: any = null;
  private _currentSession: any = null;
  /** Secondary session used during attended (consultative) transfer */
  private consultSession: any = null;
  private listeners: Map<string, Set<EventHandler>> = new Map();
  private status: SipClientStatus = "idle";
  private remoteAudioEl: HTMLAudioElement | null = null;
  /** Separate audio element for consultative call to avoid conflicts with held primary call */
  private consultAudioEl: HTMLAudioElement | null = null;
  private config: SipConfig | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectDelay = 30000;
  private healthCheckTimer: ReturnType<typeof setInterval> | null = null;
  private visibilityHandler: (() => void) | null = null;

  /** SessionManager — manages multiple concurrent SIP sessions */
  private sessionManager: SessionManager = new SessionManager();

  /** AudioMixer — Web Audio API-based mixer for conference calls */
  private audioMixer: AudioMixer | null = null;

  /** Original sender tracks stored before conference for restoration on split */
  private originalSenderTracks: Map<string, MediaStreamTrack> = new Map();

  /** Tracks the ID of the one allowed waiting (ringing) call */
  private waitingCallId: string | null = null;

  /**
   * Backward-compatible getter: returns the raw JsSIP session for the
   * currently active managed session, or falls back to the legacy
   * _currentSession field if SessionManager has no active session.
   */
  get currentSession(): any {
    const activeManaged = this.sessionManager.getActiveSession();
    if (activeManaged) {
      return activeManaged.session;
    }
    return this._currentSession;
  }

  set currentSession(value: any) {
    this._currentSession = value;
  }

  // ---------- Event emitter ----------
  on(event: SipClientEvent, handler: EventHandler): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  off(event: SipClientEvent, handler: EventHandler): void {
    this.listeners.get(event)?.delete(handler);
  }

  private emit(event: SipClientEvent, ...args: any[]): void {
    this.listeners.get(event)?.forEach((handler) => {
      try {
        handler(...args);
      } catch (e) {
        console.error(`[SipClient] Error in ${event} handler:`, e);
      }
    });
  }

  // ---------- Lifecycle ----------
  setRemoteAudioElement(el: HTMLAudioElement): void {
    this.remoteAudioEl = el;
  }

  setConsultAudioElement(el: HTMLAudioElement): void {
    this.consultAudioEl = el;
  }

  getStatus(): SipClientStatus {
    return this.status;
  }

  getCurrentCall(): SipCallInfo | null {
    if (!this.currentSession) return null;
    return {
      id: this.currentSession.id,
      direction: this.currentSession.direction === "incoming" ? "inbound" : "outbound",
      remoteIdentity:
        this.currentSession.remote_identity?.uri?.user ||
        this.currentSession.remote_identity?.display_name ||
        "Unknown",
      displayName:
        this.currentSession.remote_identity?.display_name || "",
      startTime: this.currentSession.start_time
        ? new Date(this.currentSession.start_time).getTime()
        : Date.now(),
      isOnHold: this.currentSession.isOnHold?.()?.local ?? false,
      isMuted: this.currentSession.isMuted?.()?.audio ?? false,
    };
  }

  register(config: SipConfig): void {
    // Tear down any existing UA
    this.teardown();

    this.config = config;
    this.setStatus("connecting");

    this.socket = new JsSIP.WebSocketInterface(config.wsServer);

    this.ua = new JsSIP.UA({
      uri: config.sipUri,
      password: config.password,
      display_name: config.displayName,
      sockets: [this.socket],
      register: true,
      session_timers: false,
      registrar_server: undefined,
      contact_uri: undefined,
      no_answer_timeout: 60,
      register_expires: 120,
    });

    this.ua.on("registered", () => {
      this.reconnectAttempts = 0;
      this.setStatus("registered");
      this.emit("registered");
    });

    this.ua.on("unregistered", () => {
      this.setStatus("unregistered");
      this.emit("unregistered");
    });

    this.ua.on("registrationFailed", (e: any) => {
      console.warn("[SipClient] Registration failed:", e?.cause);
      this.setStatus("failed");
      this.emit("registrationFailed", e?.cause || "Unknown error");
      // Attempt reconnection on registration failure
      this.handleDisconnect();
    });

    this.ua.on("newRTCSession", (e: any) => {
      const session = e.session;

      if (session.direction === "incoming") {
        console.log("[SipClient] Incoming call from:", session.remote_identity?.uri?.user);

        // --- Multi-call logic ---
        // If at max sessions, reject with 486
        if (!this.sessionManager.canAddSession()) {
          console.log("[SipClient] Rejecting incoming call — at max sessions (4)");
          session.terminate({ status_code: 486 });
          return;
        }

        // If a waiting call already exists, reject with 486
        if (this.waitingCallId) {
          console.log("[SipClient] Rejecting incoming call — a waiting call already exists");
          session.terminate({ status_code: 486 });
          return;
        }

        // Reject incoming calls when we have an outbound call in progress to the same
        // number (PBX may loop the INVITE back to other registered endpoints of the
        // same extension). This prevents the ringtone from playing on outgoing calls.
        const incomingNumber = session.remote_identity?.uri?.user || "";
        const existingOutbound = this.sessionManager.getAllSessions().find(
          (s) => s.direction === "outbound" &&
            s.remoteIdentity === incomingNumber &&
            s.status === "active"
        );
        if (existingOutbound) {
          console.log("[SipClient] Rejecting incoming call — outbound call in progress to same number:", incomingNumber);
          session.terminate({ status_code: 486 });
          return;
        }

        // Also reject if we have an active consult call (attended transfer in progress)
        if (this.consultSession) {
          const isConsultEnded = typeof this.consultSession.isEnded === "function"
            ? this.consultSession.isEnded()
            : (this.consultSession.status >= 8);
          if (!isConsultEnded) {
            console.log("[SipClient] Rejecting incoming call — attended transfer in progress");
            session.terminate({ status_code: 486 });
            return;
          }
          this.consultSession = null;
        }

        // Add session to SessionManager in "ringing" state
        const remoteIdentity =
          session.remote_identity?.uri?.user ||
          session.remote_identity?.display_name ||
          "";
        const displayName = session.remote_identity?.display_name || "";

        const managed = this.sessionManager.addSession(session, "inbound", {
          remoteIdentity,
          displayName,
          id: session.id,
        });

        if (!managed) {
          // Shouldn't happen since we checked canAddSession, but guard anyway
          console.log("[SipClient] Rejecting incoming call — SessionManager could not add session");
          session.terminate({ status_code: 486 });
          return;
        }

        // If there's already an active call, mark this as the waiting call
        const activeSession = this.sessionManager.getActiveSession();
        if (activeSession && activeSession.id !== managed.id) {
          this.waitingCallId = managed.id;
        }

        // Legacy: keep _currentSession pointing to the active session for backward compat
        // Only set _currentSession if this is the first/only session
        if (this.sessionManager.getSessionCount() === 1) {
          this._currentSession = session;
        }

        this.attachSessionEvents(session);

        // Emit sessionAdded so WebrtcProvider adds this to Redux sessions dict
        this.emit("sessionAdded", managed);

        // Build call info from the INCOMING session, not the active session.
        // getCurrentCall() would return call A's info when call B arrives.
        const incomingCallInfo: SipCallInfo = {
          id: managed.id,
          direction: "inbound",
          remoteIdentity: remoteIdentity || "Unknown",
          displayName: displayName,
          startTime: managed.startTime,
          isOnHold: false,
          isMuted: false,
        };
        console.log("[SipClient] Emitting incomingCall event:", incomingCallInfo);
        this.emit("incomingCall", incomingCallInfo);
      }
    });

    this.ua.on("disconnected", () => {
      if (this.status !== "idle") {
        this.handleDisconnect();
      }
    });

    this.ua.start();

    // Start health check to detect silent connection drops
    this.startHealthCheck();

    // Listen for tab visibility changes to re-check registration
    this.startVisibilityHandler();
  }

  unregister(): void {
    this.clearReconnectTimer();
    this.stopHealthCheck();
    this.stopVisibilityHandler();
    // Tear down conference if active
    if (this.audioMixer) {
      this.audioMixer.destroy();
      this.audioMixer = null;
      this.originalSenderTracks.clear();
    }
    if (this.ua) {
      this.ua.unregister({ all: true });
      this.ua.stop();
    }
    this.ua = null;
    this.socket = null;
    this.config = null;
    this._currentSession = null;
    this.waitingCallId = null;
    // Remove all sessions from SessionManager
    this.sessionManager.getAllSessions().forEach((s) => {
      this.sessionManager.removeSession(s.id);
    });
    this.setStatus("idle");
  }

  updateCredentials(newPassword: string): void {
    if (this.config) {
      this.config = { ...this.config, password: newPassword };
      // Re-register with new password
      if (this.ua) {
        this.ua.stop();
      }
      this.register(this.config);
    }
  }

  reconnect(): void {
    if (this.config) {
      this.register(this.config);
    }
  }

  // ---------- Call controls ----------
  /**
   * Place an outbound call.
   *
   * @param number  Destination to dial.
   *
   * The SIP INVITE carries no CRM identifiers. Any CRM call-linking context is
   * tracked entirely server-side (see the MirtaPBX call-context flow in
   * useCallButton), so this method's audio/session pipeline is unchanged.
   */
  call(number: string): void {
    if (!this.ua || this.status !== "registered") {
      console.warn("[SipClient] Cannot call — not registered");
      return;
    }

    // Multi-call: check if we can add another session
    if (!this.sessionManager.canAddSession()) {
      console.warn("[SipClient] Cannot call — maximum session limit reached (4)");
      this.emit("multiCallError", "Maximum session limit reached. Please end a call before placing a new one.");
      return;
    }

    const options = {
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: this.config?.iceServers || [],
        iceTransportPolicy: "all" as RTCIceTransportPolicy,
        iceCandidatePoolSize: 10,
      },
      rtcOfferConstraints: {
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      },
      sessionTimersExpires: 120,
    };

    console.log("[SipClient] Placing call to:", number);
    const session = this.ua.call(`sip:${number}@${this.getDomain()}`, options);

    // Add to SessionManager
    const remoteIdentity = number;
    const managed = this.sessionManager.addSession(session, "outbound", {
      remoteIdentity,
      displayName: "",
      id: session.id,
    });

    if (managed) {
      // Update status to active for outgoing calls (they're immediately connecting)
      this.sessionManager.updateSession(managed.id, { status: "active" });
    }

    // Legacy: keep _currentSession for backward compat
    this._currentSession = session;
    this.attachSessionEvents(session);
    this.emit("sessionAdded", managed);
    this.emit("calling"); // Signal that call is being placed
  }

  answer(): void {
    // If there's a waiting call, answer that one (it's the ringing session
    // shown in IncomingCallToast). Otherwise fall back to the current session.
    if (this.waitingCallId) {
      this.acceptWaitingCall();
      return;
    }

    if (!this.currentSession) {
      console.warn("[SipClient] No active session to answer");
      return;
    }

    // JsSIP status codes: 3 = STATUS_INVITE_RECEIVED, 4 = STATUS_WAITING_FOR_ANSWER
    // Both are valid states to call answer() on for incoming calls.
    const status = this.currentSession.status;
    if (status !== 4 && status !== 3) {
      console.warn(`[SipClient] Cannot answer — session status is ${status} (expected 3 or 4)`);
      return;
    }

    this.currentSession.answer({
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: this.config?.iceServers || [],
        iceTransportPolicy: "all" as RTCIceTransportPolicy,
        iceCandidatePoolSize: 10,
      },
    });

    // Update session status in SessionManager from "ringing" to "active"
    if (this.currentSession.id) {
      this.sessionManager.updateSession(this.currentSession.id, { status: "active" });
      this.emit("sessionUpdated", this.sessionManager.getSession(this.currentSession.id));
    }
  }

  reject(): void {
    // If there's a waiting call, reject that one (it's the ringing session
    // shown in IncomingCallToast). Otherwise fall back to the current session.
    if (this.waitingCallId) {
      this.rejectWaitingCall();
      return;
    }

    if (!this.currentSession) return;
    const session = this.currentSession;
    session.terminate({ status_code: 486 });
    // Remove from SessionManager
    if (session.id) {
      if (this.waitingCallId === session.id) {
        this.waitingCallId = null;
      }
      this.sessionManager.removeSession(session.id);
    }
    // Legacy compat: update _currentSession
    if (this.sessionManager.getSessionCount() === 0) {
      this._currentSession = null;
    } else {
      const newActive = this.sessionManager.getActiveSession();
      if (newActive) {
        this._currentSession = newActive.session;
      }
    }
  }

  hangup(): void {
    if (!this.currentSession) return;
    const session = this.currentSession;
    session.terminate();
    // Remove from SessionManager
    if (session.id) {
      if (this.waitingCallId === session.id) {
        this.waitingCallId = null;
      }
      this.sessionManager.removeSession(session.id);
    }
    // Legacy compat: update _currentSession
    if (this.sessionManager.getSessionCount() === 0) {
      this._currentSession = null;
    } else {
      const newActive = this.sessionManager.getActiveSession();
      if (newActive) {
        this._currentSession = newActive.session;
      }
    }
  }

  hold(): void {
    if (!this.currentSession) return;
    const holdResult = this.currentSession.isOnHold?.();
    if (holdResult?.local) {
      console.log("[SipClient] Already on hold");
      return;
    }
    console.log("[SipClient] Placing call on hold");
    this.currentSession.hold();
  }

  unhold(): void {
    if (!this.currentSession) return;
    const holdResult = this.currentSession.isOnHold?.();
    if (!holdResult?.local) {
      console.log("[SipClient] Not on hold, nothing to resume");
      return;
    }
    console.log("[SipClient] Resuming call from hold");
    this.currentSession.unhold();
  }

  isOnHold(): boolean {
    if (!this.currentSession) return false;
    return this.currentSession.isOnHold?.()?.local ?? false;
  }

  mute(): void {
    if (!this.currentSession) return;
    this.currentSession.mute({ audio: true });
    this.emit("callMute");
  }

  unmute(): void {
    if (!this.currentSession) return;
    this.currentSession.unmute({ audio: true });
    this.emit("callUnmute");
  }

  sendDTMF(digit: string): void {
    if (!this.currentSession) return;
    this.currentSession.sendDTMF(digit, {
      duration: 100,
      interToneGap: 50,
    });
  }

  /**
   * Blind (unattended) transfer — sends a SIP REFER to move the active call
   * to the specified target extension. The current call will end once the
   * REFER is accepted by the PBX.
   */
  blindTransfer(target: string): boolean {
    if (!this.currentSession) {
      console.warn("[SipClient] No active session to transfer");
      return false;
    }

    const domain = this.getDomain();
    if (!domain) {
      console.warn("[SipClient] Cannot determine SIP domain for transfer");
      return false;
    }

    const referUri = `sip:${target}@${domain}`;
    console.log("[SipClient] Blind transfer to:", referUri);

    try {
      this.currentSession.refer(referUri);
      return true;
    } catch (e) {
      console.error("[SipClient] Blind transfer failed:", e);
      return false;
    }
  }

  // ---------- Attended (Consultative) Transfer ----------

  /**
   * Step 1: Start an attended transfer.
   * Places the current call on hold, then initiates a consultative call
   * to the target extension.
   */
  startAttendedTransfer(target: string): boolean {
    if (!this.currentSession) {
      console.warn("[SipClient] No active session for attended transfer");
      return false;
    }

    if (!this.ua || this.status !== "registered") {
      console.warn("[SipClient] Cannot start attended transfer — not registered");
      return false;
    }

    const domain = this.getDomain();
    if (!domain) {
      console.warn("[SipClient] Cannot determine SIP domain for attended transfer");
      return false;
    }

    // Put the current (primary) call on hold
    const holdResult = this.currentSession.isOnHold?.();
    if (!holdResult?.local) {
      console.log("[SipClient] Putting primary call on hold for attended transfer");
      this.currentSession.hold();
    }

    // Place a consultative call to the target
    const options = {
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: this.config?.iceServers || [],
        iceTransportPolicy: "all" as RTCIceTransportPolicy,
        iceCandidatePoolSize: 10,
      },
      rtcOfferConstraints: {
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      },
      sessionTimersExpires: 120,
    };

    const targetUri = `sip:${target}@${domain}`;
    console.log("[SipClient] Placing consultative call to:", targetUri);

    try {
      const session = this.ua.call(targetUri, options);
      this.consultSession = session;
      this.attachConsultSessionEvents(session);
      this.emit("consultCallStarted", target);
      return true;
    } catch (e) {
      console.error("[SipClient] Failed to start consultative call:", e);
      // Unhold the primary call since consult failed
      this.currentSession.unhold();
      return false;
    }
  }

  /**
   * Step 2: Complete the attended transfer.
   *
   * Per SIP attended transfer spec (RFC 5589):
   * - A (client) is on hold with B (us) — this is the primary session
   * - B (us) has a consult call with C (target) — this is the consult session
   * - REFER is sent on the A↔B leg (primary session)
   * - The Refer-To URI points to C, with Replaces= referencing the B↔C call
   * - Replaces header uses B↔C's Call-ID, with to-tag=C's tag, from-tag=B's tag
   * - PBX bridges A↔C and sends BYE to both B's legs
   */
  completeAttendedTransfer(): boolean {
    if (!this.currentSession || !this.consultSession) {
      console.warn("[SipClient] Cannot complete attended transfer — missing sessions");
      return false;
    }

    const domain = this.getDomain();
    if (!domain) {
      console.warn("[SipClient] Cannot determine SIP domain for transfer");
      return false;
    }

    try {
      const targetUser = this.consultSession.remote_identity?.uri?.user || "";
      const targetUri = `sip:${targetUser}@${domain}`;
      // Extract our local user from sipUri (e.g. "sip:1001@cloudphone.one" → "1001")
      const localUserMatch = this.config?.sipUri.match(/^sip:([^@]+)@/);
      const localUser = localUserMatch?.[1] || "";
      const referredByHeader = localUser
        ? `Referred-By: <sip:${localUser}@${domain}>`
        : "";

      console.log("[SipClient] Sending REFER on primary (A↔B) with Replaces →", targetUri);

      const cleanupOnSuccess = () => {
        try {
          this.currentSession?.terminate();
        } catch {}
        try {
          this.consultSession?.terminate();
        } catch {}
        // Clean up from SessionManager
        const activeManaged = this.sessionManager.getActiveSession();
        if (activeManaged) {
          this.sessionManager.removeSession(activeManaged.id);
        }
        this._currentSession = null;
        this.consultSession = null;
        if (this.consultAudioEl) this.consultAudioEl.srcObject = null;
        this.emit("attendedTransferCompleted");
        this.emit("callEnded");
      };

      const cleanupOnFailure = (cause: string) => {
        console.warn("[SipClient] Attended transfer failed:", cause);
        this.emit("attendedTransferFailed", cause);
        // Resume the primary (held) call so the user isn't stuck
        try {
          if (this.currentSession?.isOnHold?.()?.local) {
            this.currentSession.unhold();
          }
        } catch {}
      };

      // Let JsSIP construct the Refer-To URI with proper Replaces escaping
      // by passing the consult session as `replaces`.
      const referSubscriber: any = this.currentSession.refer(targetUri, {
        replaces: this.consultSession,
        extraHeaders: referredByHeader ? [referredByHeader] : [],
        eventHandlers: {
          requestSucceeded: () => {
            console.log("[SipClient] REFER accepted by PBX (2xx to REFER)");
          },
          requestFailed: (data: any) => {
            cleanupOnFailure(data?.cause || "REFER rejected");
          },
          trying: () => {
            console.log("[SipClient] REFER NOTIFY: 100 Trying");
          },
          progress: () => {
            console.log("[SipClient] REFER NOTIFY: progress");
          },
          accepted: () => {
            console.log("[SipClient] REFER NOTIFY: 2xx — transfer succeeded");
            cleanupOnSuccess();
          },
          failed: (data: any) => {
            cleanupOnFailure(data?.cause || "Transfer target rejected the call");
          },
        },
      });

      // Older JsSIP versions may not honor eventHandlers passed inline —
      // attach as event listeners on the returned subscriber as a fallback.
      if (referSubscriber && typeof referSubscriber.on === "function") {
        referSubscriber.on("requestFailed", (data: any) =>
          cleanupOnFailure(data?.cause || "REFER rejected")
        );
        referSubscriber.on("accepted", () => cleanupOnSuccess());
        referSubscriber.on("failed", (data: any) =>
          cleanupOnFailure(data?.cause || "Transfer target rejected the call")
        );
      }

      return true;
    } catch (e) {
      console.error("[SipClient] Attended transfer completion threw:", e);
      this.emit("attendedTransferFailed", "Transfer failed");
      return false;
    }
  }

  /**
   * Cancel the attended transfer — hang up the consultative call
   * and resume the primary (held) call.
   */
  cancelAttendedTransfer(): void {
    if (this.consultSession) {
      console.log("[SipClient] Cancelling attended transfer — hanging up consult call");
      try {
        this.consultSession.terminate();
      } catch {
        // Ignore if already terminated
      }
      this.consultSession = null;
    }

    // Clean up consult audio and restore primary audio
    if (this.consultAudioEl) {
      this.consultAudioEl.srcObject = null;
    }

    // Resume the primary call and re-attach its audio
    if (this.currentSession) {
      const holdResult = this.currentSession.isOnHold?.();
      if (holdResult?.local) {
        console.log("[SipClient] Resuming primary call after cancelled transfer");
        this.currentSession.unhold();
      }
      // Re-attach primary call audio since unhold triggers re-INVITE
      // The audio will be re-attached via the existing peerconnection track events
    }
  }

  /**
   * Whether a consultative (attended transfer) call is currently active.
   */
  hasConsultCall(): boolean {
    if (!this.consultSession) return false;
    const isEnded = typeof this.consultSession.isEnded === "function"
      ? this.consultSession.isEnded()
      : (this.consultSession.status >= 8);
    return !isEnded;
  }

  /**
   * Get info about the consultative call.
   */
  getConsultCall(): SipCallInfo | null {
    if (!this.consultSession) return null;
    return {
      id: this.consultSession.id,
      direction: "outbound",
      remoteIdentity:
        this.consultSession.remote_identity?.uri?.user ||
        this.consultSession.remote_identity?.display_name ||
        "Unknown",
      displayName:
        this.consultSession.remote_identity?.display_name || "",
      startTime: this.consultSession.start_time
        ? new Date(this.consultSession.start_time).getTime()
        : Date.now(),
      isOnHold: this.consultSession.isOnHold?.()?.local ?? false,
      isMuted: this.consultSession.isMuted?.()?.audio ?? false,
    };
  }

  /**
   * Attach events to the consultative session for attended transfer.
   */
  private attachConsultSessionEvents(session: any): void {
    // Use the same ICE short-circuit logic as the main session
    let sawHost = false;
    let sawSrflx = false;
    let readyCalled = false;
    let iceShortCircuitTimer: ReturnType<typeof setTimeout> | null = null;
    const callReady = (readyFn: () => void, reason: string) => {
      if (readyCalled) return;
      readyCalled = true;
      if (iceShortCircuitTimer) {
        clearTimeout(iceShortCircuitTimer);
        iceShortCircuitTimer = null;
      }
      console.log(`[SipClient] Consult ICE ready (${reason})`);
      readyFn();
    };

    session.on("icecandidate", (data: any) => {
      const candidate: RTCIceCandidate | undefined = data?.candidate;
      const ready: (() => void) | undefined = data?.ready;
      if (!candidate || typeof ready !== "function") return;
      const type = candidate.type || "";
      if (type === "host") sawHost = true;
      if (type === "srflx") sawSrflx = true;
      if (sawHost && sawSrflx) {
        callReady(ready, "host+srflx gathered");
        return;
      }
      if (!iceShortCircuitTimer) {
        iceShortCircuitTimer = setTimeout(() => {
          if (sawHost || sawSrflx) {
            callReady(ready, "timeout with partial candidates");
          }
        }, 3000);
      }
    });

    session.on("peerconnection", (data: any) => {
      const pc: RTCPeerConnection = data.peerconnection;

      pc.oniceconnectionstatechange = () => {
        console.log("[SipClient] Consult ICE state:", pc.iceConnectionState);
      };

      // Route consult call audio to a dedicated audio element so it doesn't
      // conflict with the held primary call's audio element
      pc.ontrack = (event: RTCTrackEvent) => {
        console.log("[SipClient] Consult ontrack fired, streams:", event.streams.length);
        const audioEl = this.consultAudioEl || this.remoteAudioEl;
        if (audioEl && event.streams?.[0]) {
          audioEl.srcObject = event.streams[0];
          audioEl.play().catch((e) => {
            console.warn("[SipClient] Consult audio play failed:", e.message);
          });
        }
      };

      (pc as any).onaddstream = (event: any) => {
        console.log("[SipClient] Consult onaddstream fired");
        const audioEl = this.consultAudioEl || this.remoteAudioEl;
        if (audioEl && event.stream) {
          audioEl.srcObject = event.stream;
          audioEl.play().catch(() => {});
        }
      };
    });

    session.on("accepted", () => {
      console.log("[SipClient] Consultative call accepted");
      this.emit("consultCallAccepted", this.getConsultCall());
    });

    // Fallback: attach audio when consult call is confirmed
    session.on("confirmed", () => {
      console.log("[SipClient] Consultative call confirmed");
      const audioEl = this.consultAudioEl || this.remoteAudioEl;
      if (audioEl && !audioEl.srcObject) {
        const pc = session.connection;
        if (pc) {
          const receivers = pc.getReceivers();
          if (receivers.length > 0) {
            const remoteStream = new MediaStream();
            receivers.forEach((receiver: RTCRtpReceiver) => {
              if (receiver.track) {
                remoteStream.addTrack(receiver.track);
              }
            });
            if (remoteStream.getTracks().length > 0) {
              console.log("[SipClient] Attached consult audio from receivers in confirmed event");
              audioEl.srcObject = remoteStream;
              audioEl.play().catch(() => {});
            }
          }
        }
      }
    });

    session.on("ended", () => {
      console.log("[SipClient] Consultative call ended");
      if (this.consultAudioEl) {
        this.consultAudioEl.srcObject = null;
      }
      this.consultSession = null;
      this.emit("consultCallEnded");
    });

    session.on("failed", (e: any) => {
      console.log("[SipClient] Consultative call failed:", e?.cause);
      if (this.consultAudioEl) {
        this.consultAudioEl.srcObject = null;
      }
      this.consultSession = null;
      this.emit("consultCallFailed", e?.cause || "Call failed");
      // Resume the primary call since the consult failed
      if (this.currentSession) {
        const holdResult = this.currentSession.isOnHold?.();
        if (holdResult?.local) {
          this.currentSession.unhold();
        }
      }
    });
  }

  // ---------- Multi-call public API ----------

  /**
   * Places an outgoing call and returns the managed session ID.
   * Returns null if the call could not be placed (e.g. at max sessions,
   * not registered).
   */
  callWithSessionId(number: string): string | null {
    if (!this.ua || this.status !== "registered") {
      console.warn("[SipClient] Cannot call — not registered");
      return null;
    }

    if (!this.sessionManager.canAddSession()) {
      console.warn("[SipClient] Cannot call — maximum session limit reached (4)");
      this.emit("maxSessionsReached");
      this.emit("multiCallError", "Maximum session limit reached. Please end a call before placing a new one.");
      return null;
    }

    const options = {
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: this.config?.iceServers || [],
        iceTransportPolicy: "all" as RTCIceTransportPolicy,
        iceCandidatePoolSize: 10,
      },
      rtcOfferConstraints: {
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      },
      sessionTimersExpires: 120,
    };

    console.log("[SipClient] Placing call (with session ID) to:", number);
    const session = this.ua.call(`sip:${number}@${this.getDomain()}`, options);

    const remoteIdentity = number;
    const managed = this.sessionManager.addSession(session, "outbound", {
      remoteIdentity,
      displayName: "",
      id: session.id,
    });

    if (!managed) {
      return null;
    }

    // Set to active for outgoing calls
    this.sessionManager.updateSession(managed.id, { status: "active" });

    // Legacy compat: keep _currentSession
    this._currentSession = session;
    this.attachSessionEvents(session);
    this.emit("calling");
    this.emit("sessionAdded", managed);

    return managed.id;
  }

  /**
   * Answers a specific session by its managed session ID.
   * If another session is currently active, it will be placed on hold first.
   */
  answerSession(sessionId: string): void {
    const managed = this.sessionManager.getSession(sessionId);
    if (!managed) {
      console.warn(`[SipClient] answerSession — session ${sessionId} not found`);
      return;
    }

    const rawSession = managed.session;

    // JsSIP status 4 = STATUS_WAITING_FOR_ANSWER
    const status = rawSession.status;
    if (status !== 4 && status !== 3) {
      console.warn(`[SipClient] answerSession — session status is ${status} (expected 3 or 4)`);
      return;
    }

    // If there's a different active session, hold it first
    const activeSession = this.sessionManager.getActiveSession();
    if (activeSession && activeSession.id !== sessionId) {
      const activeRaw = activeSession.session;
      const holdResult = activeRaw.isOnHold?.();
      if (!holdResult?.local) {
        console.log("[SipClient] answerSession — holding current active session before answering");
        activeRaw.hold();
        this.sessionManager.updateSession(activeSession.id, {
          status: "held",
          holdStartTime: Date.now(),
        });
        this.emit("sessionUpdated", this.sessionManager.getSession(activeSession.id));
      }
    }

    rawSession.answer({
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: this.config?.iceServers || [],
        iceTransportPolicy: "all" as RTCIceTransportPolicy,
        iceCandidatePoolSize: 10,
      },
    });

    // Update state: session becomes active
    this.sessionManager.updateSession(sessionId, { status: "active" });
    this.sessionManager.setActiveSession(sessionId);

    // Clear waiting call reference if this was the waiting call
    if (this.waitingCallId === sessionId) {
      this.waitingCallId = null;
    }

    // Legacy compat
    this._currentSession = rawSession;
    this.emit("activeSessionChanged", sessionId);
    this.emit("sessionUpdated", this.sessionManager.getSession(sessionId));
  }

  /**
   * Rejects/terminates a specific session by its managed session ID.
   */
  rejectSession(sessionId: string): void {
    const managed = this.sessionManager.getSession(sessionId);
    if (!managed) {
      console.warn(`[SipClient] rejectSession — session ${sessionId} not found`);
      return;
    }

    managed.session.terminate({ status_code: 486 });

    // Clean up waiting call reference
    if (this.waitingCallId === sessionId) {
      this.waitingCallId = null;
    }

    this.sessionManager.removeSession(sessionId);
    this.emit("sessionRemoved", sessionId);

    // Legacy compat: update _currentSession
    if (this.sessionManager.getSessionCount() === 0) {
      this._currentSession = null;
    } else {
      const newActive = this.sessionManager.getActiveSession();
      if (newActive) {
        this._currentSession = newActive.session;
      }
    }
  }

  /**
   * Hangs up a specific session by its managed session ID.
   */
  hangupSession(sessionId: string): void {
    const managed = this.sessionManager.getSession(sessionId);
    if (!managed) {
      console.warn(`[SipClient] hangupSession — session ${sessionId} not found`);
      return;
    }

    managed.session.terminate();

    // Clean up waiting call reference
    if (this.waitingCallId === sessionId) {
      this.waitingCallId = null;
    }

    this.sessionManager.removeSession(sessionId);
    this.emit("sessionRemoved", sessionId);

    // Legacy compat: update _currentSession
    if (this.sessionManager.getSessionCount() === 0) {
      this._currentSession = null;
    } else {
      const newActive = this.sessionManager.getActiveSession();
      if (newActive) {
        this._currentSession = newActive.session;
      }
    }
  }

  /**
   * Places a specific session on hold by its managed session ID.
   * Delegates to SessionManager.holdSession which handles the SIP re-INVITE
   * and emits appropriate events on success/failure.
   */
  holdSession(sessionId: string): void {
    const managed = this.sessionManager.getSession(sessionId);
    if (!managed) {
      console.warn(`[SipClient] holdSession — session ${sessionId} not found`);
      return;
    }

    // No-op if already held (Req 3.9)
    if (managed.status === "held") {
      console.log(`[SipClient] holdSession — session ${sessionId} already on hold`);
      return;
    }

    // Use direct JsSIP hold() — don't use Promise-based SessionManager.holdSession()
    // because JsSIP eventHandler callbacks may not fire on all PBX configurations.
    try {
      managed.session.hold();
      this.sessionManager.updateSession(sessionId, { status: "held", holdStartTime: Date.now() });
      this.emit("callHold");
      this.emit("sessionUpdated", this.sessionManager.getSession(sessionId));
    } catch (error: any) {
      console.error(`[SipClient] holdSession failed for ${sessionId}:`, error?.message);
      this.emit("holdFailed", sessionId, error?.message || "Hold failed");
    }
  }

  /**
   * Resumes a specific held session by its managed session ID.
   * If another session is currently active, it will be held first.
   */
  unholdSession(sessionId: string): void {
    const managed = this.sessionManager.getSession(sessionId);
    if (!managed) {
      console.warn(`[SipClient] unholdSession — session ${sessionId} not found`);
      return;
    }

    // If there's a different active session, hold it first (Req 3.4)
    const activeSession = this.sessionManager.getActiveSession();
    if (activeSession && activeSession.id !== sessionId && activeSession.status === "active") {
      try {
        activeSession.session.hold();
        this.sessionManager.updateSession(activeSession.id, { status: "held", holdStartTime: Date.now() });
        this.emit("sessionUpdated", this.sessionManager.getSession(activeSession.id));
      } catch (error: any) {
        console.error(`[SipClient] unholdSession — failed to hold active session:`, error?.message);
        this.emit("resumeFailed", sessionId, "Failed to hold current active session");
        return;
      }
    }

    // Resume the target session
    try {
      managed.session.unhold();
      this.sessionManager.updateSession(sessionId, { status: "active", holdStartTime: null });
      this.sessionManager.setActiveSession(sessionId);
      this._currentSession = managed.session;
      this.emit("callUnhold");
      this.emit("activeSessionChanged", sessionId);
      this.emit("sessionUpdated", this.sessionManager.getSession(sessionId));
    } catch (error: any) {
      console.error(`[SipClient] unholdSession failed for ${sessionId}:`, error?.message);
      this.emit("resumeFailed", sessionId, error?.message || "Resume failed");
    }
  }

  /**
   * Swaps to a target session — holds the current active call and resumes
   * the target session.
   *
   * No-op if only one session exists (Req 8.6).
   */
  async swapToSession(targetSessionId: string): Promise<void> {
    // No-op with single session (Req 8.6)
    if (this.sessionManager.getSessionCount() <= 1) {
      console.log("[SipClient] swapToSession — only one session exists, no-op");
      return;
    }

    const target = this.sessionManager.getSession(targetSessionId);
    if (!target) {
      console.warn(`[SipClient] swapToSession — target session ${targetSessionId} not found`);
      return;
    }

    // No-op if target is already the active session
    const activeSession = this.sessionManager.getActiveSession();
    if (activeSession && activeSession.id === targetSessionId) {
      console.log("[SipClient] swapToSession — target is already active, no-op");
      return;
    }

    this.emit("swapStarted", activeSession?.id, targetSessionId);

    // Hold current active session
    if (activeSession && activeSession.status === "active") {
      try {
        activeSession.session.hold();
        this.sessionManager.updateSession(activeSession.id, { status: "held", holdStartTime: Date.now() });
        this.emit("sessionUpdated", this.sessionManager.getSession(activeSession.id));
      } catch (error: any) {
        console.error("[SipClient] swapToSession — hold failed:", error?.message);
        this.emit("swapFailed", "Failed to hold current call");
        return;
      }
    }

    // Resume target session
    try {
      target.session.unhold();
      this.sessionManager.updateSession(targetSessionId, { status: "active", holdStartTime: null });
      this.sessionManager.setActiveSession(targetSessionId);
      this._currentSession = target.session;
      this.emit("swapCompleted", activeSession?.id, targetSessionId);
      this.emit("activeSessionChanged", targetSessionId);
      this.emit("sessionUpdated", this.sessionManager.getSession(targetSessionId));
    } catch (error: any) {
      console.error("[SipClient] swapToSession — resume failed:", error?.message);
      this.emit("swapFailed", "Failed to resume target call");
      // Try to recover original
      if (activeSession) {
        try {
          activeSession.session.unhold();
          this.sessionManager.updateSession(activeSession.id, { status: "active", holdStartTime: null });
          this.emit("sessionUpdated", this.sessionManager.getSession(activeSession.id));
        } catch {
          this.emit("swapCriticalFailure", "Manual reconnection required");
        }
      }
    }
  }

  // ---------- SessionManager access ----------

  /**
   * Returns the internal SessionManager for multi-call operations.
   */
  getSessionManager(): SessionManager {
    return this.sessionManager;
  }

  // ---------- Conference (merge/split) ----------

  /**
   * Returns the current AudioMixer instance, or null if no conference is active.
   */
  getAudioMixer(): AudioMixer | null {
    return this.audioMixer;
  }

  /**
   * Merge 2-6 sessions into a local audio conference.
   *
   * Flow:
   * 1. Validate: require 2-6 sessions, all must exist in SessionManager
   * 2. Resume any held sessions (Req 5.4); if resume fails, exclude that session (Req 5.12)
   * 3. Create AudioMixer
   * 4. For each session: add remote stream as participant
   * 5. Set local mic stream on mixer
   * 6. For each session: get per-session sub-mix, replace sender track
   * 7. Update statuses to "merging"
   * 8. Emit "conferenceStarted"
   *
   * No-op if only one session exists (Req 8.6).
   */
  async mergeSessionIds(sessionIds: string[]): Promise<void> {
    // Req 8.6: no-op with single session
    if (this.sessionManager.getSessionCount() <= 1) {
      console.log("[SipClient] mergeSessionIds — only one session exists, no-op");
      return;
    }

    // Validate: require 2-6 sessions
    if (sessionIds.length < 2 || sessionIds.length > 6) {
      console.warn(`[SipClient] mergeSessionIds — invalid count: ${sessionIds.length} (need 2-6)`);
      this.emit("multiCallError", "Merge requires between 2 and 6 sessions");
      return;
    }

    // Validate all sessions exist
    const validSessions = sessionIds
      .map((id) => this.sessionManager.getSession(id))
      .filter((s) => s !== undefined);

    if (validSessions.length !== sessionIds.length) {
      console.warn("[SipClient] mergeSessionIds — some sessions not found");
      this.emit("multiCallError", "Some sessions could not be found for merge");
      return;
    }

    // Resume any held sessions (Req 5.4), exclude failures (Req 5.12)
    // Use direct rawSession.unhold() instead of Promise-based sessionManager.unholdSession()
    // because JsSIP may not fire eventHandler callbacks on all PBX configurations.
    const successfulSessionIds: string[] = [];
    const failedSessionIds: string[] = [];

    for (const managed of validSessions) {
      if (managed!.status === "held") {
        try {
          const rawSession = managed!.session;
          rawSession.unhold();
          this.sessionManager.updateSession(managed!.id, { status: "active", holdStartTime: null });
          this.emit("sessionUpdated", this.sessionManager.getSession(managed!.id));
          successfulSessionIds.push(managed!.id);
        } catch (error: any) {
          console.warn(`[SipClient] mergeSessionIds — failed to resume session ${managed!.id}:`, error?.message);
          failedSessionIds.push(managed!.id);
          this.emit("multiCallError", `Failed to resume session ${managed!.remoteIdentity || managed!.id} for merge`);
        }
      } else {
        successfulSessionIds.push(managed!.id);
      }
    }

    // If we have fewer than 2 successful sessions, abort
    if (successfulSessionIds.length < 2) {
      console.warn("[SipClient] mergeSessionIds — fewer than 2 sessions available after resume attempts");
      this.emit("multiCallError", "Could not merge: fewer than 2 sessions available after resume failures");
      return;
    }

    // Create AudioMixer
    const mixer = new AudioMixer();
    mixer.create();
    this.audioMixer = mixer;

    // Get local mic stream from one of the sessions' RTCPeerConnection sender tracks
    let localMicStream: MediaStream | null = null;

    for (const sessionId of successfulSessionIds) {
      const managed = this.sessionManager.getSession(sessionId);
      if (!managed) continue;

      const pc: RTCPeerConnection | null = managed.session.connection;
      if (!pc) continue;

      // Get remote stream for this session
      const receivers = pc.getReceivers();
      const remoteStream = new MediaStream();
      receivers.forEach((receiver: RTCRtpReceiver) => {
        if (receiver.track) {
          remoteStream.addTrack(receiver.track);
        }
      });

      if (remoteStream.getAudioTracks().length > 0) {
        mixer.addParticipant(sessionId, remoteStream);
      }

      // Get local mic from sender tracks (only need it once)
      if (!localMicStream) {
        const senders = pc.getSenders();
        for (const sender of senders) {
          if (sender.track && sender.track.kind === "audio") {
            // Store original sender track for restoration on split
            this.originalSenderTracks.set(sessionId, sender.track);
            // Create a stream from the mic track
            localMicStream = new MediaStream([sender.track]);
            break;
          }
        }
      } else {
        // Store original sender track for other sessions too
        const senders = pc.getSenders();
        for (const sender of senders) {
          if (sender.track && sender.track.kind === "audio") {
            this.originalSenderTracks.set(sessionId, sender.track);
            break;
          }
        }
      }
    }

    // Set local mic on mixer
    if (localMicStream) {
      mixer.setLocalMicStream(localMicStream);
    }

    // For each session: get per-session sub-mix and replace sender track
    for (const sessionId of successfulSessionIds) {
      const managed = this.sessionManager.getSession(sessionId);
      if (!managed) continue;

      const pc: RTCPeerConnection | null = managed.session.connection;
      if (!pc) continue;

      const mixedStream = mixer.getMixedStreamForSession(sessionId);
      const mixedAudioTrack = mixedStream.getAudioTracks()[0];

      if (mixedAudioTrack) {
        const senders = pc.getSenders();
        for (const sender of senders) {
          if (sender.track && sender.track.kind === "audio") {
            try {
              await sender.replaceTrack(mixedAudioTrack);
            } catch (err: any) {
              console.warn(`[SipClient] mergeSessionIds — replaceTrack failed for ${sessionId}:`, err?.message);
            }
            break;
          }
        }
      }

      // Update status to "merging"
      this.sessionManager.updateSession(sessionId, { status: "merging" });
    }

    // Emit conferenceStarted
    const participantIds = [...successfulSessionIds];
    this.emit("conferenceStarted", {
      active: true,
      participantIds,
      startTime: Date.now(),
    });

    // Emit errors for failed sessions
    if (failedSessionIds.length > 0) {
      this.emit("conferenceUpdated", {
        active: true,
        participantIds,
        excludedIds: failedSessionIds,
      });
    }

    console.log(`[SipClient] Conference started with ${participantIds.length} participants`);
  }

  /**
   * Remove a participant from the active conference.
   *
   * Flow:
   * 1. Disconnect participant from AudioMixer
   * 2. Restore that session's original sender track
   * 3. Place session on hold (if not already ended)
   * 4. If only 1 participant remains → call splitConference()
   * 5. Emit "conferenceUpdated"
   *
   * Also handles remote hangup: if session was already ended, just clean from mixer.
   */
  async removeFromConference(sessionId: string): Promise<void> {
    if (!this.audioMixer) {
      console.warn("[SipClient] removeFromConference — no active conference");
      return;
    }

    // Remove from mixer
    this.audioMixer.removeParticipant(sessionId);

    // Restore original sender track
    await this.restoreOriginalTrack(sessionId);

    // Place session on hold (only if it still exists and isn't ended)
    const managed = this.sessionManager.getSession(sessionId);
    if (managed && managed.status !== "ended") {
      try {
        managed.session.hold();
        this.sessionManager.updateSession(sessionId, { status: "held", holdStartTime: Date.now() });
        this.emit("sessionUpdated", this.sessionManager.getSession(sessionId));
      } catch (error: any) {
        console.warn(`[SipClient] removeFromConference — hold failed for ${sessionId}:`, error?.message);
      }
    }

    // Clean up original track reference
    this.originalSenderTracks.delete(sessionId);

    // Check remaining participant count
    const remainingCount = this.audioMixer.getParticipantCount();

    if (remainingCount <= 1) {
      // Tear down conference entirely
      await this.splitConference();
    } else {
      // Emit conferenceUpdated with remaining participants
      const participantIds = this.audioMixer.getParticipantIds();
      this.emit("conferenceUpdated", {
        active: true,
        participantIds,
      });
      console.log(`[SipClient] Removed ${sessionId} from conference, ${remainingCount} remaining`);
    }
  }

  /**
   * Split the conference — disconnect all participants from the mixer,
   * restore original tracks, place non-active sessions on hold, tear down AudioContext.
   *
   * Flow:
   * 1. Disconnect all participants from AudioMixer
   * 2. Restore all original sender tracks
   * 3. Place all sessions except the designated active on hold
   * 4. Destroy AudioMixer
   * 5. Emit "conferenceEnded"
   */
  async splitConference(): Promise<void> {
    if (!this.audioMixer) {
      console.warn("[SipClient] splitConference — no active conference");
      return;
    }

    const participantIds = this.audioMixer.getParticipantIds();
    const activeSessionId = this.sessionManager.getActiveSessionId();

    // Restore all original sender tracks and disconnect from mixer
    for (const sessionId of participantIds) {
      this.audioMixer.removeParticipant(sessionId);
      await this.restoreOriginalTrack(sessionId);
    }

    // Destroy the AudioMixer
    this.audioMixer.destroy();
    this.audioMixer = null;

    // Update session statuses: active session stays active, others go on hold
    for (const sessionId of participantIds) {
      const managed = this.sessionManager.getSession(sessionId);
      if (!managed) continue;

      if (sessionId === activeSessionId) {
        this.sessionManager.updateSession(sessionId, { status: "active" });
        this.emit("sessionUpdated", this.sessionManager.getSession(sessionId));
      } else {
        // Place non-active sessions on hold using direct JsSIP call
        try {
          managed.session.hold();
          this.sessionManager.updateSession(sessionId, { status: "held", holdStartTime: Date.now() });
          this.emit("sessionUpdated", this.sessionManager.getSession(sessionId));
        } catch (error: any) {
          console.warn(`[SipClient] splitConference — hold failed for ${sessionId}:`, error?.message);
          this.sessionManager.updateSession(sessionId, { status: "held", holdStartTime: Date.now() });
          this.emit("sessionUpdated", this.sessionManager.getSession(sessionId));
        }
      }
    }

    // Clear original track storage
    this.originalSenderTracks.clear();

    // Emit conferenceEnded
    this.emit("conferenceEnded", {
      active: false,
      participantIds: [],
      endTime: Date.now(),
    });

    console.log("[SipClient] Conference ended, all sessions restored to individual calls");
  }

  /**
   * Handle remote hangup of a participant during an active conference.
   * Called from session "ended" event when a conference is active.
   */
  private handleConferenceParticipantHangup(sessionId: string): void {
    if (!this.audioMixer) return;

    // Check if this session is actually in the conference
    const participantIds = this.audioMixer.getParticipantIds();
    if (!participantIds.includes(sessionId)) return;

    console.log(`[SipClient] Conference participant ${sessionId} hung up remotely`);

    // Remove from mixer (no need to hold since they've disconnected)
    this.audioMixer.removeParticipant(sessionId);
    this.originalSenderTracks.delete(sessionId);

    // Check remaining count
    const remainingCount = this.audioMixer.getParticipantCount();

    if (remainingCount <= 1) {
      // Tear down conference
      this.splitConference();
    } else {
      // Emit update
      const remainingIds = this.audioMixer.getParticipantIds();
      this.emit("conferenceUpdated", {
        active: true,
        participantIds: remainingIds,
      });
    }
  }

  /**
   * Restore the original sender track for a session after conference.
   */
  private async restoreOriginalTrack(sessionId: string): Promise<void> {
    const originalTrack = this.originalSenderTracks.get(sessionId);
    if (!originalTrack) return;

    const managed = this.sessionManager.getSession(sessionId);
    if (!managed) return;

    const pc: RTCPeerConnection | null = managed.session.connection;
    if (!pc) return;

    const senders = pc.getSenders();
    for (const sender of senders) {
      if (sender.track && sender.track.kind === "audio") {
        try {
          await sender.replaceTrack(originalTrack);
        } catch (err: any) {
          console.warn(`[SipClient] restoreOriginalTrack — replaceTrack failed for ${sessionId}:`, err?.message);
        }
        break;
      }
    }
  }

  /**
   * Returns the ID of the waiting (ringing) call, or null if none.
   */
  getWaitingCallId(): string | null {
    return this.waitingCallId;
  }

  // ---------- Call Waiting Actions ----------

  /**
   * Accept the waiting call.
   *
   * Flow (Req 2.4):
   * 1. Place the current Active_Call on hold via SessionManager.holdSession()
   * 2. Answer the waiting session and make it the new Active_Call
   * 3. Clear waitingCallId
   *
   * If hold fails (Req 2.9):
   * - Keep the Active_Call unchanged
   * - Reject the waiting call with SIP 486
   * - Emit a holdFailed error via "multiCallError"
   */
  async acceptWaitingCall(): Promise<void> {
    const waitingId = this.waitingCallId;
    if (!waitingId) {
      console.warn("[SipClient] acceptWaitingCall — no waiting call");
      return;
    }

    const waitingManaged = this.sessionManager.getSession(waitingId);
    if (!waitingManaged) {
      console.warn("[SipClient] acceptWaitingCall — waiting session not found in SessionManager");
      this.waitingCallId = null;
      return;
    }

    const activeSession = this.sessionManager.getActiveSession();
    if (!activeSession) {
      // No active call to hold — just answer the waiting call directly
      this.answerSession(waitingId);
      return;
    }

    // Step 1: Place the current active call on hold.
    // Use a direct hold() call rather than the Promise-based SessionManager.holdSession()
    // because JsSIP may not fire the eventHandler callbacks on all PBX configurations,
    // which would cause the Promise to hang. We proceed immediately to answer the
    // waiting call — the hold re-INVITE will complete asynchronously.
    const activeRaw = activeSession.session;
    const holdResult = activeRaw.isOnHold?.();
    if (!holdResult?.local) {
      try {
        activeRaw.hold();
        this.sessionManager.updateSession(activeSession.id, {
          status: "held",
          holdStartTime: Date.now(),
        });
        this.emit("sessionUpdated", this.sessionManager.getSession(activeSession.id));
      } catch (error: any) {
        // Hold failed (Req 2.9): keep active unchanged, reject waiting call, emit error
        console.error("[SipClient] acceptWaitingCall — hold failed, rejecting waiting call:", error?.message);
        waitingManaged.session.terminate({ status_code: 486 });
        this.sessionManager.removeSession(waitingId);
        this.waitingCallId = null;
        this.emit("sessionRemoved", waitingId);
        this.emit("holdFailed", activeSession.id, error?.message || "Hold operation failed");
        this.emit("multiCallError", "Unable to accept the waiting call — hold operation failed on the current call.");
        return;
      }
    }

    // Step 2: Answer the waiting call
    const rawSession = waitingManaged.session;

    // JsSIP session status for incoming calls:
    // 3 = STATUS_INVITE_RECEIVED (initial state for incoming)
    // 4 = STATUS_WAITING_FOR_ANSWER (after provisional 180 sent)
    // Both are valid states to call answer() on.
    const status = rawSession.status;
    if (status !== 4 && status !== 3) {
      console.warn(`[SipClient] acceptWaitingCall — waiting session status is ${status} (expected 3 or 4)`);
      this.waitingCallId = null;
      return;
    }

    rawSession.answer({
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: this.config?.iceServers || [],
        iceTransportPolicy: "all" as RTCIceTransportPolicy,
        iceCandidatePoolSize: 10,
      },
    });

    // Update state: waiting session becomes active
    this.sessionManager.updateSession(waitingId, { status: "active" });
    this.sessionManager.setActiveSession(waitingId);

    // Step 3: Clear waitingCallId
    this.waitingCallId = null;

    // Legacy compat
    this._currentSession = rawSession;
    this.emit("activeSessionChanged", waitingId);
    this.emit("sessionUpdated", this.sessionManager.getSession(waitingId));
  }

  /**
   * Reject the waiting call.
   *
   * Flow (Req 2.5):
   * 1. Terminate the waiting session with SIP 486
   * 2. Remove from SessionManager
   * 3. Clear waitingCallId
   */
  rejectWaitingCall(): void {
    const waitingId = this.waitingCallId;
    if (!waitingId) {
      console.warn("[SipClient] rejectWaitingCall — no waiting call");
      return;
    }

    const waitingManaged = this.sessionManager.getSession(waitingId);
    if (!waitingManaged) {
      console.warn("[SipClient] rejectWaitingCall — waiting session not found in SessionManager");
      this.waitingCallId = null;
      return;
    }

    // Terminate with 486 Busy Here
    waitingManaged.session.terminate({ status_code: 486 });

    // Remove from SessionManager
    this.sessionManager.removeSession(waitingId);
    this.emit("sessionRemoved", waitingId);

    // Clear waitingCallId
    this.waitingCallId = null;

    // Legacy compat: update _currentSession if needed
    if (this.sessionManager.getSessionCount() === 0) {
      this._currentSession = null;
    } else {
      const newActive = this.sessionManager.getActiveSession();
      if (newActive) {
        this._currentSession = newActive.session;
      }
    }
  }

  /**
   * Ignore the waiting call.
   *
   * Flow (Req 2.6):
   * - Clear waitingCallId so the UI dismisses the notification
   * - Leave the session in "ringing" state in the SessionManager
   * - The 30s no-answer timeout or remote hangup will handle cleanup
   */
  ignoreWaitingCall(): void {
    if (!this.waitingCallId) {
      console.warn("[SipClient] ignoreWaitingCall — no waiting call");
      return;
    }

    console.log("[SipClient] ignoreWaitingCall — leaving session in ringing state");
    // Clear the waitingCallId reference. The session stays in SessionManager
    // with status "ringing" until the remote party hangs up or the 30s timeout
    // expires (handled by JsSIP's no_answer_timeout or remote BYE).
    this.waitingCallId = null;
  }

  // ---------- Private helpers ----------
  private getDomain(): string {
    if (!this.config) return "";
    // Extract domain from sipUri: "sip:1001@cloudphone.one" → "cloudphone.one"
    const match = this.config.sipUri.match(/@(.+)$/);
    return match ? match[1] : "";
  }

  private setStatus(newStatus: SipClientStatus): void {
    this.status = newStatus;
    this.emit("statusChanged", newStatus);
  }

  /**
   * Persist the remote MediaStream for a given raw JsSIP session onto its
   * ManagedSession so the per-session AudioElementPool can (re)attach it on
   * hold/resume/swap. Without this, ManagedSession.remoteStream stays null and
   * resuming a held call reattaches nothing — the resumed call has no audio.
   *
   * The AudioElementPool is authoritative for playback. We only touch the
   * legacy shared remoteAudioEl for the single-call case to preserve backward
   * compatibility; when multiple sessions exist, per-session pool elements are
   * used exclusively to avoid every session's ontrack fighting over one element.
   */
  private captureRemoteStream(session: any, stream: MediaStream): void {
    const sessionId = session?.id;
    if (!sessionId) return;

    const managed = this.sessionManager.getSession(sessionId);
    if (managed) {
      // Store the stream so WebrtcProvider's pool can reattach on resume/swap.
      this.sessionManager.updateSession(sessionId, { remoteStream: stream });
      // Notify listeners so the per-session pool element (re)attaches and plays.
      this.emit("remoteStreamUpdated", sessionId, stream);
      // The AudioElementPool is authoritative for playback. We intentionally do
      // NOT drive the shared remoteAudioEl here: every session's ontrack would
      // otherwise overwrite that single element, breaking audio when switching
      // between calls (and causing double audio for the active call).
      return;
    }

    // Fallback for sessions not tracked by the SessionManager (should not happen
    // in normal flow): use the legacy shared element so audio still plays.
    if (this.remoteAudioEl) {
      this.remoteAudioEl.srcObject = stream;
      this.remoteAudioEl.play().catch((e) => {
        console.warn("[SipClient] Audio play failed (autoplay policy):", e.message);
      });
    }
  }

  private attachSessionEvents(session: any): void {
    // Short-circuit JsSIP's wait-for-complete ICE gathering. JsSIP emits the
    // 'icecandidate' event on the session with { candidate, ready }; calling
    // ready() dispatches the SDP offer/answer immediately. We fire it as soon
    // as we have at least one host + srflx candidate, or after 3s if partial.
    let sawHost = false;
    let sawSrflx = false;
    let readyCalled = false;
    let iceShortCircuitTimer: ReturnType<typeof setTimeout> | null = null;
    const callReady = (readyFn: () => void, reason: string) => {
      if (readyCalled) return;
      readyCalled = true;
      if (iceShortCircuitTimer) {
        clearTimeout(iceShortCircuitTimer);
        iceShortCircuitTimer = null;
      }
      console.log(`[SipClient] Forcing ICE ready (${reason})`);
      readyFn();
    };

    session.on("icecandidate", (data: any) => {
      const candidate: RTCIceCandidate | undefined = data?.candidate;
      const ready: (() => void) | undefined = data?.ready;
      if (!candidate || typeof ready !== "function") return;
      const type = candidate.type || "";
      if (type === "host") sawHost = true;
      if (type === "srflx") sawSrflx = true;
      if (sawHost && sawSrflx) {
        callReady(ready, "host+srflx gathered");
        return;
      }
      if (!iceShortCircuitTimer) {
        iceShortCircuitTimer = setTimeout(() => {
          if (sawHost || sawSrflx) {
            callReady(ready, "timeout with partial candidates");
          }
        }, 3000);
      }
    });

    session.on("peerconnection", (data: any) => {
      const pc: RTCPeerConnection = data.peerconnection;

      // Log ICE connection state for debugging
      pc.oniceconnectionstatechange = () => {
        console.log("[SipClient] ICE state:", pc.iceConnectionState);
        this.emit("iceStateChanged", pc.iceConnectionState);
        if (pc.iceConnectionState === "failed") {
          console.warn("[SipClient] ICE connection failed — media will not flow");
        }
      };

      pc.onicegatheringstatechange = () => {
        console.log("[SipClient] ICE gathering state:", pc.iceGatheringState);
      };

      // Primary: ontrack (modern browsers)
      pc.ontrack = (event: RTCTrackEvent) => {
        console.log("[SipClient] ontrack fired, streams:", event.streams.length);
        const stream = event.streams?.[0];
        if (stream) {
          this.captureRemoteStream(session, stream);
        }
      };

      // Fallback: onaddstream (older WebRTC implementations)
      (pc as any).onaddstream = (event: any) => {
        console.log("[SipClient] onaddstream fired");
        if (event.stream) {
          this.captureRemoteStream(session, event.stream);
        }
      };
    });

    // Additional fallback: attach audio when call is confirmed
    session.on("confirmed", () => {
      console.log("[SipClient] Call confirmed");
      this.emit("callConfirmed");
      // Try to get remote stream from the connection if not already captured
      const managed = this.sessionManager.getSession(session.id);
      if (!managed || !managed.remoteStream) {
        const pc = session.connection;
        if (pc) {
          const receivers = pc.getReceivers();
          if (receivers.length > 0) {
            const remoteStream = new MediaStream();
            receivers.forEach((receiver: RTCRtpReceiver) => {
              if (receiver.track) {
                remoteStream.addTrack(receiver.track);
              }
            });
            if (remoteStream.getTracks().length > 0) {
              console.log("[SipClient] Attached audio from receivers in confirmed event");
              this.captureRemoteStream(session, remoteStream);
            }
          }
        }
      }
    });

    session.on("accepted", () => {
      console.log("[SipClient] Call accepted");
      this.emit("callAccepted", this.getCurrentCall());
    });

    session.on("hold", (data: any) => {
      console.log("[SipClient] Call put on hold, originator:", data?.originator);
      this.emit("callHold");
    });

    session.on("unhold", (data: any) => {
      console.log("[SipClient] Call resumed from hold, originator:", data?.originator);
      this.emit("callUnhold");
    });

    session.on("ended", () => {
      console.log("[SipClient] Call ended");
      // Clean up from SessionManager
      const sessionId = session.id;

      // Handle conference participant hangup (Req 5.8)
      if (this.audioMixer) {
        this.handleConferenceParticipantHangup(sessionId);
      }

      if (this.waitingCallId === sessionId) {
        this.waitingCallId = null;
      }
      this.sessionManager.removeSession(sessionId);
      this.emit("sessionRemoved", sessionId);
      // Legacy compat: update _currentSession
      if (this.sessionManager.getSessionCount() === 0) {
        this._currentSession = null;
      } else {
        const newActive = this.sessionManager.getActiveSession();
        if (newActive) {
          this._currentSession = newActive.session;
        }
      }
      if (this.remoteAudioEl) {
        this.remoteAudioEl.srcObject = null;
      }
      this.emit("callEnded");
    });

    session.on("failed", (e: any) => {
      console.log("[SipClient] Call failed:", e?.cause);
      // Clean up from SessionManager
      const sessionId = session.id;

      // Handle conference participant failure (Req 5.8)
      if (this.audioMixer) {
        this.handleConferenceParticipantHangup(sessionId);
      }

      if (this.waitingCallId === sessionId) {
        this.waitingCallId = null;
      }
      this.sessionManager.removeSession(sessionId);
      this.emit("sessionRemoved", sessionId);
      // Legacy compat: update _currentSession
      if (this.sessionManager.getSessionCount() === 0) {
        this._currentSession = null;
      } else {
        const newActive = this.sessionManager.getActiveSession();
        if (newActive) {
          this._currentSession = newActive.session;
        }
      }
      if (this.remoteAudioEl) {
        this.remoteAudioEl.srcObject = null;
      }
      this.emit("callFailed", e?.cause || "Call failed");
    });
  }

  private handleDisconnect(): void {
    this.setStatus("connecting");
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer();
    const delay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );
    this.reconnectAttempts++;

    console.log(`[SipClient] Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      if (this.config && this.status !== "registered") {
        console.log("[SipClient] Attempting reconnect...");
        this.reconnect();
      }
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Periodically checks if the SIP UA is still registered.
   * If registration has silently dropped (e.g., WebSocket died without close frame),
   * triggers a reconnection.
   */
  private startHealthCheck(): void {
    this.stopHealthCheck();
    this.healthCheckTimer = setInterval(() => {
      if (!this.ua || !this.config) return;

      // Check if UA thinks it's registered but the WebSocket is actually dead
      const isRegistered = this.ua.isRegistered();
      const isConnected = this.ua.isConnected();

      if (this.status === "registered" && !isRegistered) {
        console.warn("[SipClient] Health check: lost registration silently, reconnecting...");
        this.handleDisconnect();
      } else if (this.status === "registered" && !isConnected) {
        console.warn("[SipClient] Health check: WebSocket disconnected silently, reconnecting...");
        this.handleDisconnect();
      }
    }, 15000); // Check every 15 seconds
  }

  private stopHealthCheck(): void {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  /**
   * When the browser tab becomes visible again after being hidden,
   * check if we're still registered and reconnect if needed.
   * Browsers may throttle/kill WebSockets for background tabs.
   */
  private startVisibilityHandler(): void {
    this.stopVisibilityHandler();
    this.visibilityHandler = () => {
      if (document.visibilityState === "visible" && this.config) {
        // Give it a moment for the connection to resume
        setTimeout(() => {
          if (!this.ua) return;
          const isRegistered = this.ua.isRegistered();
          const isConnected = this.ua.isConnected();

          if (!isRegistered || !isConnected) {
            console.log("[SipClient] Tab became visible — re-registering (was disconnected/unregistered)");
            this.reconnect();
          } else {
            console.log("[SipClient] Tab became visible — still registered, no action needed");
          }
        }, 1000);
      }
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  private stopVisibilityHandler(): void {
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  private teardown(): void {
    this.clearReconnectTimer();
    this.stopHealthCheck();
    this.stopVisibilityHandler();
    // Tear down conference if active
    if (this.audioMixer) {
      this.audioMixer.destroy();
      this.audioMixer = null;
      this.originalSenderTracks.clear();
    }
    if (this.consultSession && !this.consultSession.isEnded?.()) {
      this.consultSession.terminate();
    }
    this.consultSession = null;
    if (this._currentSession && !this._currentSession.isEnded?.()) {
      this._currentSession.terminate();
    }
    // Clean up all managed sessions
    this.sessionManager.getAllSessions().forEach((s) => {
      this.sessionManager.removeSession(s.id);
    });
    this.waitingCallId = null;
    if (this.ua) {
      try {
        this.ua.stop();
      } catch {
        // Ignore stop errors on teardown
      }
    }
    this.ua = null;
    this.socket = null;
    this._currentSession = null;
  }
}

// Singleton export
export const sipClient = new SipClient();
